import React, { useState } from 'react';
import {
  AppInput,
  AppRemixIcon,
  AppSingleSelect,
  AppMultiSelect,
  AppDeleteModal,
} from '@/layout/AppComponents';
import styled from 'styled-components';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import ReactDOMServer from 'react-dom/server';
import { axiosPath } from '../../../../../../../routing/helpers/router';
import Translator from '../../../../../../components/helpers/Translator';
import colors from '../../../../../../../constants/colors';
import * as currentPageAction from '../../../../../../../store/actions/currentPageAction';
import useIsMounted from '../../../../../../components/hooks/useIsMounted';

const CustomMessage = (props) => {
  const { plantObligationID, type } = props;
  const { moduleBridgeHex, plantHex } = useParams();
  const isMounted = useIsMounted();
  const dispatch = useDispatch();
  const langs = useSelector((s) => s.module?.[moduleBridgeHex]?.lang);
  const customObligationTextsTabs = useSelector(
    (s) => s.currentPage?.customObligationTextsTabs
  );
  const customObligationTexts = useSelector(
    (s) => s.currentPage?.customObligationTextsTabs?.[plantObligationID]
  );
  const poLevel2 = useSelector(
    (s) => s.currentPage?.plantObligationLevels?.level2
  );
  const customObligationTextsPermissions = useSelector(
    (s) =>
      s.module?.[moduleBridgeHex]?.permissions?.gcl?.module?.plant
        ?.customObligationTexts
  );
  const [remove, setRemove] = useState();

  // > ADD
  const handleAdd = async (values, { resetForm }) => {
    try {
      const response = await axios.post(
        axiosPath(
          "api_r_user_module_legalCompliance_customObligationTexts_add"
        ),
        { moduleBridgeHex, plantHex, plantObligationID, type, ...values }
      );
      if (isMounted.current) {
        dispatch(
          currentPageAction.change(
            'customObligationTextsTabs',
            {
              ...customObligationTextsTabs,
              [plantObligationID]: {
                ...customObligationTextsTabs?.[plantObligationID],
                ...response.data.customObligationTextsTabs,
              },
            },
            true
          )
        );
        dispatch(
          currentPageAction.change('plantObligationLevels', {
            level2: {
              ...poLevel2,
              [plantObligationID]: {
                ...poLevel2?.[plantObligationID],
                obligation: {
                  ...poLevel2?.[plantObligationID].obligation,
                  [type === 'evidence' ? type : 'periodicity']: response.data
                    .level2ObligationType,
                },
              },
            },
          })
        );
        resetForm();
      }
    } catch (err) {}
  };
  // < ADD

  // < REMOVE
  const handleRemove = async () => {
    try {
      const response = await axios.post(
        axiosPath(
          "api_r_user_module_legalCompliance_customObligationTexts_remove"
        ),
        { moduleBridgeHex, plantHex, plantObligationID, type, langSlug: remove }
      );
      if (isMounted.current) {
        dispatch(
          currentPageAction.change(
            'customObligationTextsTabs',
            {
              ...customObligationTextsTabs,
              [plantObligationID]: {
                ...customObligationTextsTabs?.[plantObligationID],
                ...response.data.customObligationTextsTabs,
              },
            },
            true
          )
        );
        dispatch(
          currentPageAction.change('plantObligationLevels', {
            level2: {
              ...poLevel2,
              [plantObligationID]: {
                ...poLevel2?.[plantObligationID],
                obligation: {
                  ...poLevel2?.[plantObligationID].obligation,
                  [type === 'evidence' ? type : 'periodicity']: response.data
                    .level2ObligationType,
                },
              },
            },
          })
        );
        setRemove(undefined);
      }
    } catch (err) {}
  };
  // > REMOVE

  // < FORM SCHEMA
  const validationSchema = Yup.object().shape({
    text: Yup.string().required(
      ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)
    ),
    language: Yup.number().required(
      ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)
    ),
    plants: Yup.number().required(
      ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)
    ),
  });
  // < FORM SCHEMA

  return (
    <>
      <div className="row">
        <div
          className={`mb-3 ${
            (type === 'nextDate' &&
              !customObligationTextsPermissions?.evidence) ||
            (type === 'evidence' && !customObligationTextsPermissions?.nextDate)
              ? 'col-md-6'
              : 'col-md-12'
          }`}
        >
          <Container>
            <Formik
              validateOnChange={false}
              validateOnBlur={false}
              enableReinitialize
              validationSchema={validationSchema}
              initialValues={{
                text: '',
                language: null,
                plants: [],
              }}
              onSubmit={handleAdd}
            >
              {({
                setFieldValue,
                handleSubmit,
                values,
                errors,
                isSubmitting,
              }) => (
                <>
                  <h3>
                    <Translator
                      file="module-legalCompliance"
                      name="customize.message"
                    />{' '}
                    (
                    <Translator
                      file="module-legalCompliance"
                      name={
                        type === 'evidence'
                          ? 'my.evidences'
                          : 'module.lc.nextDate'
                      }
                    />
                    )
                    <span
                      className="btn btn-success btn-xs float-right"
                      onClick={() =>
                        isSubmitting ? undefined : handleSubmit()
                      }
                      disabled={isSubmitting}
                    >
                      <Translator file="module-legalCompliance" name="add" />
                    </span>
                    <div className="clearfix" />
                  </h3>
                  <AppInput
                    icon={<AppRemixIcon icon="double-quotes-l" noVariants />}
                    title={
                      <Translator
                        file="module-legalCompliance"
                        name="new.text"
                      />
                    }
                    value={values.text}
                    onChange={(v) => setFieldValue('text', v)}
                    error={!!errors.text}
                    required
                    isClearable
                  />
                  <AppSingleSelect
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="language.text"
                      />
                    )}
                    icon={<AppRemixIcon icon="earth" />}
                    value={values.language}
                    options={langs?.map((l) => ({ value: l.id, label: l.name }))}
                    onChange={(v) => setFieldValue('language', v)}
                    error={!!errors.language}
                    hasSearchBox
                  />
                  <AppMultiSelect
                    title={ReactDOMServer.renderToString(
                      <Translator file="user" name="user.plants" />
                    )}
                    icon={<AppRemixIcon icon="building-3" />}
                    value={values.plants}
                    options={customObligationTexts?.userPlants}
                    onChange={(v) => setFieldValue('plants', v)}
                    error={!!errors.plants}
                    isLoading={!customObligationTexts?.userPlants}
                    hasSearchBox
                    hasSelectOptions
                  />
                </>
              )}
            </Formik>
          </Container>
        </div>
        <div
          className={`mb-3 ${
            (type === 'nextDate' &&
              !customObligationTextsPermissions?.evidence) ||
            (type === 'evidence' && !customObligationTextsPermissions?.nextDate)
              ? 'col-md-6'
              : 'col-md-12'
          }`}
        >
          <Container>
            <h3>
              <Translator
                file="module-legalCompliance"
                name="existing.list.translations"
              />{' '}
              (
              <Translator
                file="module-legalCompliance"
                name={
                  type === 'evidence' ? 'my.evidences' : 'module.lc.nextDate'
                }
              />
              )
            </h3>
            {customObligationTexts?.[type] &&
            customObligationTexts?.[type].length > 0 ? (
              <table className="table">
                <thead>
                  <tr>
                    <th className="col-lg-8"></th>
                    <th className="col-lg-2"></th>
                    <th className="col-lg-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {customObligationTexts[type].map((cot) => (
                    <tr key={cot.langSlug}>
                      <td>{cot.text}</td>
                      <td className="text-center">
                        {langs?.find((l) => l.slug === cot.langSlug).name}
                      </td>
                      <td className="text-center">
                        <span
                          className="pointer"
                          onClick={() => setRemove(cot.langSlug)}
                          title={ReactDOMServer.renderToString(
                            <Translator file="user" name="user.delete" />
                          )}
                        >
                          <AppRemixIcon
                            icon="delete-bin-6"
                            color={colors.danger}
                          />
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="alert alert-info">
                <Translator
                  file="module-legalCompliance"
                  name="existing.list.translations"
                />
              </div>
            )}
          </Container>
        </div>
      </div>
      <AppDeleteModal
        show={!!remove}
        title={<Translator file="user" name="user.remove" />}
        onConfirm={handleRemove}
        onClose={() => setRemove(undefined)}
      />
    </>
  );
};
export default CustomMessage;

const Container = styled.div`
  border: 1px solid ${colors.grey300};
  padding: 10px;
`;
